import { Alert, alertClasses, AlertTitle, Button, Collapse, Grid2, Typography } from "@mui/material";
import { AuthenticatedUserContext } from "@ngt/opms";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useRouteError } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { createSentryAnonymousUser } from "../../errors/errors";

const RouteErrorBoundary : FC = () => {
    // Get error from the route context.
    const error = useRouteError() as Error;

    // Retrieve user information
    // Based on how the application handles the global user object:
    //     - redux
    //     - react context
    //     - etc
    // There may be a number of interactions that have to take place to ensure
    // that the unloading of the route and showing of this error boundary
    // have not unloaded the user object from its data store or from Sentry.
    const { 
        data: user, 
        loading: userLoading, 
        validating: userValidating 
    } = useContext(AuthenticatedUserContext);

    // Flag to ensure that the error is only logged once
    // More important for development with StrictMode, but if user SWR to load
    // the user, its possible that a interval update or update on focus is in
    // use which could cause multiple submissions for this single error.
    const errorLogged = useRef(false);

    useEffect(() => {
        // Early exit while waiting on the user object to load / be checked.
        if (userLoading || userValidating) {
            return;
        }

        // Early exit as the error has already been processed.
        if (errorLogged.current) {
            return;
        }

        // Get the current Sentry scope.
        const scope =  Sentry.getCurrentScope();

        // Get the current Sentry user.
        const sentryUser = Sentry.getCurrentScope().getUser();

        // If the Sentry user does not match the current user then update
        // the user on the current Sentry scope to the current user 
        // information.
        if (user && (!sentryUser || sentryUser.id !== user.userName)) {
            scope.setUser({
                id: user.userName,
                username: user.userName,
                fullName: user.displayName,
                authenticated: true
            })
        }

        // If there is not a current user but the current Sentry scope has
        // a user the update the user on the current Sentry scope to be an
        // anonymous user.
        if (!user && sentryUser && sentryUser.authenticated) {
            scope.setUser(createSentryAnonymousUser())
        }

        // Capture exception using current scope.
        Sentry.captureException(error);

        // Flag error as processed so it does not get processed again.
        errorLogged.current = true;
    }, [error, userLoading, userValidating, errorLogged]);
    
    const [details, setDetails] = useState(false);
  
    return (
        <>
            <Alert
                sx={{
                    margin: 2,
                    [`& .${alertClasses.message}`]: {
                        width: "100%"
                    }
                }}
                severity="error"
            >
                <AlertTitle>An error has occurred!</AlertTitle>
                <Typography gutterBottom>
                    Please refresh the page and try again.
                </Typography>
                {
                    error?.message && (
                        <Typography gutterBottom>
                            <strong>Error:</strong> <em>{error.message}</em>
                        </Typography>
                    )
                }
                <Grid2
                    container
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        width: "100%"
                    }}
                    gap={2}
                >
                    <Grid2>
                        {
                            error?.stack && (
                                <>
                                    <Button onClick={() => setDetails(c => !c)}>
                                        More Details
                                    </Button>
                                </>
                            )
                        }
                    </Grid2>
                    <Grid2>
                        <Button
                            component={Link}
                            to={"/"}
                            variant="contained"
                        >
                            Home
                        </Button>
                    </Grid2>
                </Grid2>
                {
                    error?.stack && (
                    <Collapse in={details}>
                        <pre>
                            {error.stack}
                        </pre>
                    </Collapse>
                    )
                }
            </Alert>
        </>
    );
}

export default RouteErrorBoundary;